import { AiTurnDataset } from "../../../models/AiAssistant";

/**
 * Removes GFM tables from assistant markdown when the rows they list are
 * already in one of the turn's datasets. The assistant is told not to repeat
 * tool rows in text, but it still does now and then — typically a handful of
 * rows with a "..." row in the middle. Those tables carry nothing the dataset
 * table under the answer does not, so they are dropped. Tables the assistant
 * computed itself (a per-pool count, a comparison) do not match dataset rows
 * and stay.
 */
export function stripRedundantTables(text: string, datasets: AiTurnDataset[] | undefined): string {
	if (!datasets?.length || text.indexOf("|") === -1) return text;

	const datasetRows = datasets.flatMap((dataset) =>
		dataset.rows.map((row) =>
			Object.values(row)
				.filter((value) => value !== null && value !== undefined && value !== "")
				.map((value) => normalize(String(value)))
		)
	);

	if (!datasetRows.length) return text;

	const lines = text.split("\n");
	const kept: string[] = [];

	for (let i = 0; i < lines.length; i++) {
		const table = tableAt(lines, i);

		if (!table) {
			kept.push(lines[i]);
			continue;
		}

		if (!isRedundant(table.body, datasetRows)) {
			kept.push(...lines.slice(i, table.end));
		} else {
			// Collapse the blank line the table left behind so paragraphs don't drift apart.
			if (
				(lines[table.end] ?? "").trim() === "" &&
				(!kept.length || kept[kept.length - 1].trim() === "")
			) {
				if (kept.length) kept.pop();
				else table.end++;
			}
		}

		i = table.end - 1;
	}

	return kept.join("\n");
}

interface TableBlock {
	/** Body rows (header + delimiter excluded) as cell texts. */
	body: string[][];
	/** Index of the first line after the table. */
	end: number;
}

const DELIMITER_ROW = /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/;

/** The table whose header starts at `index`, if there is one. */
function tableAt(lines: string[], index: number): TableBlock | undefined {
	const header = lines[index];
	const delimiter = lines[index + 1];

	// A delimiter without pipes ("---" under a paragraph) is a setext heading, not a table.
	if (!header || header.indexOf("|") === -1 || !delimiter || delimiter.indexOf("|") === -1)
		return undefined;
	if (!DELIMITER_ROW.test(delimiter)) return undefined;

	const body: string[][] = [];
	let end = index + 2;

	while (end < lines.length && lines[end].trim() !== "" && lines[end].indexOf("|") !== -1) {
		body.push(cells(lines[end]));
		end++;
	}

	return { body, end };
}

/** A "..." row is the assistant's own truncation marker; it carries no data. */
function isEllipsisRow(row: string[]): boolean {
	return row.every((cell) => cell === "" || cell === "..." || cell === "…" || cell === "-");
}

/**
 * True when at least half of the data rows are found in the datasets. A row
 * is found when one dataset row contains at least two of its cells (all of
 * them for one-column tables) — a single shared name is not enough, since a
 * summary table the assistant computed could share that.
 */
function isRedundant(body: string[][], datasetRows: string[][]): boolean {
	const dataRows = body.filter((row) => !isEllipsisRow(row));

	if (!dataRows.length) return body.length > 0;

	const covered = dataRows.filter((row) =>
		datasetRows.some((values) => rowMatches(row, values))
	).length;

	return covered * 2 >= dataRows.length;
}

function rowMatches(row: string[], values: string[]): boolean {
	const cellsToMatch = row.filter(
		(cell) => cell !== "" && cell !== "..." && cell !== "…" && cell !== "-"
	);
	if (!cellsToMatch.length) return false;

	const matched = cellsToMatch.filter((cell) =>
		values.some((value) => cellMatches(cell, value))
	).length;
	const required = cellsToMatch.length === 1 ? 1 : Math.max(2, Math.ceil(cellsToMatch.length / 2));

	return matched >= required;
}

function cellMatches(cell: string, value: string): boolean {
	if (cell === value) return true;
	// Dates rendered without the time part, numbers without decimals, etc.
	return cell.length >= 3 && value.indexOf(cell) !== -1;
}

/** Cell texts of one table row, markdown emphasis and links reduced to their text. */
function cells(line: string): string[] {
	let trimmed = line.trim();
	if (trimmed.startsWith("|")) trimmed = trimmed.substring(1);
	if (trimmed.endsWith("|") && !trimmed.endsWith("\\|"))
		trimmed = trimmed.substring(0, trimmed.length - 1);

	return trimmed.split(/(?<!\\)\|/).map((cell) => normalize(cell));
}

function normalize(value: string): string {
	return value
		.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
		.replace(/[*_`]/g, "")
		.replace(/\\\|/g, "|")
		.replace(/\s+/g, " ")
		.trim()
		.toLowerCase();
}
